import React from 'react';
import { US_STATES, VERTICALS, RESPONSE_TIMES } from './constants';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';

interface Props {
  data: any;
  onChange: (data: any) => void;
}

/** Human-readable vertical labels */
const VERTICAL_LABELS: Record<string, string> = {
  plumbing: 'Plumbing',
  hvac: 'HVAC / Heating & Cooling',
  roofing: 'Roofing',
  electrical: 'Electrical',
  landscaping: 'Landscaping',
  cleaning: 'Cleaning',
};

/** What each feature toggle does on the live site */
const FEATURE_DESCRIPTIONS: Record<string, { label: string; hint: string }> = {
  showBlog:       { label: 'Show blog section', hint: 'Adds a "Tips & Articles" section to your homepage' },
  showWorks:      { label: 'Show past projects gallery', hint: 'Displays a photo gallery of completed jobs' },
  showFinancing:  { label: 'Show financing options', hint: 'Highlights payment plan / financing info on the site' },
  emergencyBadge: { label: 'Show "24/7 Emergency" badge', hint: 'Displays a badge on the header so visitors know you\'re available around the clock' },
  stickyPhone:    { label: 'Sticky call button on mobile', hint: 'Keeps your phone number pinned at the bottom of the screen on phones' },
};

const BusinessEditor: React.FC<Props> = ({ data, onChange }) => {
  const form = data || {};

  const set = (path: string, value: any) => {
    const clone = JSON.parse(JSON.stringify(form));
    const keys = path.split('.');
    let obj = clone;
    for (let i = 0; i < keys.length - 1; i++) {
      if (!obj[keys[i]]) obj[keys[i]] = {};
      obj = obj[keys[i]];
    }
    obj[keys[keys.length - 1]] = value;
    onChange(clone);
  };

  const get = (path: string) => path.split('.').reduce((o: any, k) => o?.[k], form) ?? '';

  const field = (label: string, path: string, opts?: { type?: string; placeholder?: string; min?: number; max?: number; step?: number; hint?: string }) => (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <Input
        type={opts?.type || 'text'}
        value={get(path)}
        onChange={(e) => {
          const val = opts?.type === 'number' ? (e.target.value === '' ? '' : Number(e.target.value)) : e.target.value;
          set(path, val);
        }}
        placeholder={opts?.placeholder}
        min={opts?.min}
        max={opts?.max}
        step={opts?.step}
      />
      {opts?.hint && <p className="text-xs text-muted">{opts.hint}</p>}
    </div>
  );

  const currentYear = new Date().getFullYear();

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-display font-semibold text-primary">Business Info</h2>
        <p className="text-sm text-muted mt-1 mb-5">Core business details shown to visitors</p>
      </div>

      <Card>
        <CardHeader><CardTitle>Your Business</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {field('Business Name', 'name', { placeholder: 'e.g. Smith Plumbing Co.' })}
          {field('Tagline', 'tagline', { placeholder: 'e.g. Trusted plumbers since 1998', hint: 'Short phrase shown under your business name on the site' })}
          {field('Phone Number', 'phone', { type: 'tel', placeholder: '(555) 123-4567', hint: 'Displayed to visitors — use the format you prefer' })}
          {field('Email', 'email', { type: 'email' })}

          <div className="space-y-1.5">
            <Label>Business Type</Label>
            <Select value={get('vertical') || undefined} onValueChange={(v) => set('vertical', v)}>
              <SelectTrigger>
                <SelectValue placeholder="Select your trade..." />
              </SelectTrigger>
              <SelectContent>
                {VERTICALS.map((v) => (
                  <SelectItem key={v} value={v}>{VERTICAL_LABELS[v] ?? v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {field('Year Established', 'established', { type: 'number', min: 1900, max: currentYear, placeholder: String(currentYear - 10), hint: 'The year your business was founded' })}

          <div className="space-y-1.5">
            <Label>How fast do you respond to new calls?</Label>
            <Select value={get('responseTime') || undefined} onValueChange={(v) => set('responseTime', v)}>
              <SelectTrigger>
                <SelectValue placeholder="Select..." />
              </SelectTrigger>
              <SelectContent>
                {RESPONSE_TIMES.map((rt) => (
                  <SelectItem key={rt} value={rt}>{rt}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Address</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {field('Street', 'address.street')}
          {field('City', 'address.city')}
          <div className="space-y-1.5">
            <Label>State</Label>
            <Select value={get('address.state') || undefined} onValueChange={(v) => set('address.state', v)}>
              <SelectTrigger><SelectValue placeholder="Select state..." /></SelectTrigger>
              <SelectContent>
                {US_STATES.map((st) => <SelectItem key={st} value={st}>{st}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {field('ZIP Code', 'address.zip', { placeholder: '85001' })}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Business Hours</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {field('Weekdays', 'hours.weekdays', { placeholder: '8:00 AM – 6:00 PM' })}
          {field('Saturday', 'hours.saturday', { placeholder: '9:00 AM – 4:00 PM' })}
          {field('Sunday', 'hours.sunday', { placeholder: 'Closed' })}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Ratings &amp; Reviews</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {field('Average Rating', 'rating', { type: 'number', min: 1, max: 5, step: 0.1, hint: 'Your Google / Yelp average (1–5)' })}
          {field('Number of Reviews', 'reviewCount', { type: 'number', min: 0 })}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>What do you offer?</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <label className="flex items-center gap-2.5 text-sm text-primary cursor-pointer select-none">
              <Checkbox checked={!!get('emergencyService')} onCheckedChange={(v) => set('emergencyService', v)} />
              Emergency service available
            </label>
            <label className="flex items-center gap-2.5 text-sm text-primary cursor-pointer select-none">
              <Checkbox checked={!!get('financing')} onCheckedChange={(v) => set('financing', v)} />
              Financing / payment plans
            </label>
            <label className="flex items-center gap-2.5 text-sm text-primary cursor-pointer select-none">
              <Checkbox checked={!!get('freeEstimates')} onCheckedChange={(v) => set('freeEstimates', v)} />
              Free estimates
            </label>
          </div>

          <p className="text-xs font-semibold text-muted uppercase tracking-wide pt-2">What sections appear on your site</p>
          <div className="space-y-3">
            {Object.entries(FEATURE_DESCRIPTIONS).map(([key, { label, hint }]) => (
              <label key={key} className="flex items-start gap-2.5 text-sm text-primary cursor-pointer select-none">
                <Checkbox
                  className="mt-0.5 shrink-0"
                  checked={!!get(`features.${key}`)}
                  onCheckedChange={(v) => set(`features.${key}`, v)}
                />
                <span>
                  <span className="font-medium">{label}</span>
                  <span className="block text-xs text-muted mt-0.5">{hint}</span>
                </span>
              </label>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Social media — collapsed by default so it doesn't overwhelm the page */}
      <details className="group">
        <summary className="flex items-center gap-2 cursor-pointer list-none rounded-lg border border-zinc-200 px-4 py-3 bg-white hover:bg-zinc-50 transition-colors">
          <svg className="w-4 h-4 text-muted transition-transform group-open:rotate-90" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="m9 18 6-6-6-6" />
          </svg>
          <span className="text-sm font-medium text-primary">Social Media Links</span>
          <span className="ml-auto text-xs text-muted">Optional</span>
        </summary>
        <div className="mt-2 rounded-lg border border-zinc-200 bg-white p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          {field('Facebook Page URL', 'socialMedia.facebook', { type: 'url', placeholder: 'https://facebook.com/yourpage' })}
          {field('Instagram URL', 'socialMedia.instagram', { type: 'url', placeholder: 'https://instagram.com/yourhandle' })}
          {field('Google Business URL', 'socialMedia.google', { type: 'url', placeholder: 'https://g.page/...' })}
          {field('Yelp Page URL', 'socialMedia.yelp', { type: 'url', placeholder: 'https://yelp.com/biz/...' })}
        </div>
      </details>

      {/* Advanced / internal fields — hidden from typical client view */}
      <details className="group">
        <summary className="flex items-center gap-2 cursor-pointer list-none rounded-lg border border-zinc-200 px-4 py-3 bg-white hover:bg-zinc-50 transition-colors">
          <svg className="w-4 h-4 text-muted transition-transform group-open:rotate-90" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="m9 18 6-6-6-6" />
          </svg>
          <span className="text-sm font-medium text-primary">Advanced</span>
          <span className="ml-auto text-xs text-muted">Usually leave as-is</span>
        </summary>
        <div className="mt-2 rounded-lg border border-zinc-200 bg-white p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          {field('Legal / registered business name', 'legalName', { hint: 'Only needed if different from your display name above' })}
          {field('Phone (machine-readable)', 'phoneRaw', { type: 'tel', placeholder: '+15551234567', hint: 'Auto-formatted — only change if the main phone number looks wrong on the site' })}
          {field('Website URL', 'website', { type: 'url', placeholder: 'https://...' })}
        </div>
      </details>
    </div>
  );
};

export default BusinessEditor;
